import type { FastifyPluginAsync } from 'fastify'
import { incidentManager } from '../../services/incident-manager.service.js'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('AdminIncidentController')

export const adminIncidentRoutes: FastifyPluginAsync = async (fastify) => {
  
  /**
   * GET /admin/incidents
   * Get all incidents with optional filters
   */
  fastify.get('/', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          status: { type: 'string' },
          severity: { type: 'string' },
          category: { type: 'string' },
          limit: { type: 'number' }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const filters = request.query as any
      const incidents = await incidentManager.getIncidents(filters)
      
      return reply.status(200).send({
        success: true,
        data: incidents
      })
    } catch (error) {
      logger.error('[GET /] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get incidents'
      })
    }
  })

  /**
   * POST /admin/incidents
   * Create a new incident
   */
  fastify.post('/', {
    schema: {
      body: {
        type: 'object',
        properties: {
          title: { type: 'string' },
          description: { type: 'string' },
          severity: { type: 'string', enum: ['low', 'medium', 'high', 'critical'] },
          category: { type: 'string' },
          affectedServices: { type: 'array', items: { type: 'string' } },
          affectedUsers: { type: 'number' },
          reportedBy: { type: 'string' },
          metadata: { type: 'object' }
        },
        required: ['title', 'description', 'severity', 'category', 'affectedServices']
      }
    }
  }, async (request, reply) => {
    try {
      const params = request.body as any
      const incident = await incidentManager.createIncident(params)
      
      return reply.status(201).send({
        success: true,
        data: incident
      })
    } catch (error) {
      logger.error('[POST /] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to create incident'
      })
    }
  })

  /**
   * PATCH /admin/incidents/:incidentId
   * Update an incident
   */
  fastify.patch('/:incidentId', {
    schema: {
      params: {
        type: 'object',
        properties: {
          incidentId: { type: 'string' }
        },
        required: ['incidentId']
      },
      body: {
        type: 'object',
        properties: {
          status: { type: 'string' },
          severity: { type: 'string' },
          assignedTo: { type: 'string' },
          metadata: { type: 'object' }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const { incidentId } = request.params as { incidentId: string }
      const update = request.body as any
      
      await incidentManager.updateIncident(incidentId, update)
      
      return reply.status(200).send({
        success: true,
        message: 'Incident updated'
      })
    } catch (error) {
      logger.error('[PATCH /:incidentId] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to update incident'
      })
    }
  })

  /**
   * POST /admin/incidents/:incidentId/updates
   * Add an update to an incident
   */
  fastify.post('/:incidentId/updates', {
    schema: {
      params: {
        type: 'object',
        properties: {
          incidentId: { type: 'string' }
        },
        required: ['incidentId']
      },
      body: {
        type: 'object',
        properties: {
          status: { type: 'string' },
          message: { type: 'string' },
          createdBy: { type: 'string' },
          isPublic: { type: 'boolean' },
          actions: { type: 'array', items: { type: 'string' } }
        },
        required: ['status', 'message', 'createdBy', 'isPublic']
      }
    }
  }, async (request, reply) => {
    try {
      const { incidentId } = request.params as { incidentId: string }
      const params = request.body as any
      
      const update = await incidentManager.addIncidentUpdate({
        incidentId,
        ...params
      })
      
      return reply.status(201).send({
        success: true,
        data: update
      })
    } catch (error) {
      logger.error('[POST /:incidentId/updates] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to add incident update'
      })
    }
  })

  /**
   * GET /admin/incidents/metrics
   * Get incident metrics
   */
  fastify.get('/metrics', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          period: { type: 'string', enum: ['day', 'week', 'month'] }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const { period = 'month' } = request.query as { period?: 'day' | 'week' | 'month' }
      const metrics = await incidentManager.getMetrics(period)
      
      return reply.status(200).send({
        success: true,
        data: metrics
      })
    } catch (error) {
      logger.error('[GET /metrics] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get metrics'
      })
    }
  })

  /**
   * GET /admin/incidents/circuit-breakers
   * Get circuit breaker states for all services
   */
  fastify.get('/circuit-breakers', async (request, reply) => {
    try {
      const services = ['openai', 'mapbox', 'google-places', 'weather', 'stripe', 'firebase', 'redis']
      const breakers = services.map(service => ({
        service,
        state: incidentManager.getCircuitBreaker(service)
      }))
      
      return reply.status(200).send({
        success: true,
        data: breakers
      })
    } catch (error) {
      logger.error('[GET /circuit-breakers] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get circuit breakers'
      })
    }
  })

  /**
   * POST /admin/incidents/circuit-breakers/:service/record
   * Record a service call result (for testing)
   */
  fastify.post('/circuit-breakers/:service/record', {
    schema: {
      params: {
        type: 'object',
        properties: {
          service: { type: 'string' }
        },
        required: ['service']
      },
      body: {
        type: 'object',
        properties: {
          success: { type: 'boolean' },
          error: { type: 'string' }
        },
        required: ['success']
      }
    }
  }, async (request, reply) => {
    try {
      const { service } = request.params as { service: string }
      const { success, error } = request.body as { success: boolean; error?: string }
      
      incidentManager.recordServiceCall(service, success, error)
      
      return reply.status(200).send({
        success: true,
        message: 'Service call recorded'
      })
    } catch (error) {
      logger.error('[POST /circuit-breakers/:service/record] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to record service call'
      })
    }
  })
}
